/*
 * Copyright (c) 2022-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { NextStep } from '@okta/okta-auth-js';
import { IdxOption } from '@okta/okta-auth-js/types/lib/idx/types/idx-js';
import { getLanguageTags } from 'util/LanguageUtil';

import {
  AuthenticatorButtonListElement,
  ButtonElement,
  ButtonType,
  DescriptionElement,
  HeadingElement,
  IdxStepTransformer,
  LinkElement,
  TitleElement,
  UISchemaElement,
} from '../../types';
import { getGracePeriodRequiredSoonCustomLink, getSupportedLanguages, loc } from '../../util';
import { getAuthenticatorEnrollButtonElements } from './utils';

const getContentDescrAndParams = (brandName?: string): TitleElement['options'] => {
  if (brandName) {
    return {
      content: loc('oie.select.authenticators.enroll.subtitle.custom', 'login', [brandName]),
    };
  }
  return { content: loc('oie.select.authenticators.enroll.subtitle', 'login') };
};

const isGracePeriodExpiryStillActive = (expiry: string): boolean => {
  const currentTimestampMs = Date.now();
  const gracePeriodTimestampMs = new Date(expiry).getTime();
  // using isNaN as ie11 does not support Number.isNaN
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(gracePeriodTimestampMs) && currentTimestampMs < gracePeriodTimestampMs;
};

export const transformSelectAuthenticatorEnroll: IdxStepTransformer = ({
  transaction,
  formBag,
  widgetProps,
}) => {
  const { uischema } = formBag;
  const { brandName } = widgetProps;
  const {
    nextStep: { inputs, name: stepName } = {} as NextStep,
    availableSteps,
    // @ts-ignore OKTA-499928 authenticatorEnrollments missing from rawIdxState
    rawIdxState: { authenticatorEnrollments },
  } = transaction;

  const authenticator = inputs?.find(({ name }) => name === 'authenticator');
  if (!authenticator?.options?.length) {
    return formBag;
  }

  const supportedLanguages = getSupportedLanguages(widgetProps);
  const languageTags = getLanguageTags(widgetProps.language, supportedLanguages);
  const authenticatorsWithGracePeriod : IdxOption[] = [];
  const authenticatorsDueNow : IdxOption[] = [];
  authenticator.options.forEach((option) => {
    // @ts-ignore TODO: Add grace period fields to auth-js SDK https://oktainc.atlassian.net/browse/OKTA-848910
    const hasActiveGracePeriodExpiry = option.relatesTo?.gracePeriod?.expiry
      // @ts-ignore TODO: Add grace period fields to auth-js SDK https://oktainc.atlassian.net/browse/OKTA-848910
      && isGracePeriodExpiryStillActive(option.relatesTo?.gracePeriod?.expiry);
    // @ts-ignore TODO: Add grace period fields to auth-js SDK https://oktainc.atlassian.net/browse/OKTA-848910
    const hasActiveRemainingSkips = option.relatesTo?.gracePeriod?.remainingSkips
      // @ts-ignore TODO: Add grace period fields to auth-js SDK https://oktainc.atlassian.net/browse/OKTA-848910
      && option.relatesTo?.gracePeriod?.remainingSkips > 0;
    if (hasActiveGracePeriodExpiry || hasActiveRemainingSkips) {
      authenticatorsWithGracePeriod.push(option);
    } else {
      authenticatorsDueNow.push(option);
    }
  });

  const authenticatorButtonsWithGracePeriod = getAuthenticatorEnrollButtonElements(
    authenticatorsWithGracePeriod,
    stepName,
    languageTags,
    authenticatorEnrollments?.value,
  );

  const authenticatorButtonsDueNow = getAuthenticatorEnrollButtonElements(
    authenticatorsDueNow,
    stepName,
    languageTags,
    authenticatorEnrollments?.value,
  );
  const skipStep = availableSteps?.find(({ name }) => name === 'skip');

  const title: TitleElement = {
    type: 'Title',
    options: {
      content: loc('oie.select.authenticators.enroll.title', 'login'),
    },
  };
  const description: DescriptionElement = {
    type: 'Description',
    contentType: 'subtitle',
    options: getContentDescrAndParams(brandName),
  };

  const headingRequiredNow: HeadingElement = {
    type: 'Heading',
    options: {
      content: loc('oie.setup.required.now', 'login'),
      level: 2,
      visualLevel: 6,
      dataSe: 'authenticator-list-title',
    },
  };

  const headingRequiredSoon: HeadingElement = {
    type: 'Heading',
    options: {
      content: loc('oie.setup.required.soon', 'login'),
      level: 2,
      visualLevel: 6,
      dataSe: 'authenticator-list-title',
    },
  };

  const headingNoGracePeriod: HeadingElement = {
    type: 'Heading',
    options: {
      content: skipStep ? loc('oie.setup.optional.short', 'login') : loc('oie.setup.required.now', 'login'),
      level: 2,
      visualLevel: 6,
    },
  };

  const descriptionGracePeriod: DescriptionElement = {
    type: 'Description',
    contentType: 'subtitle',
    options: {
      content: loc('oie.setup.required.soon.description', 'login'),
    },
  };

  const customLink = getGracePeriodRequiredSoonCustomLink(widgetProps);
  let gracePeriodRequiredSoonCustomLink: LinkElement | undefined;
  if (customLink?.href && customLink?.text) {
    gracePeriodRequiredSoonCustomLink = {
      type: 'Link',
      options: {
        href: customLink.href,
        target: '_blank',
        step: '',
        label: customLink.text,
        dataSe: 'gracePeriodRequiredSoonCustomLink',
      },
    };
  }

  const authenticatorListElementWithGracePeriod: AuthenticatorButtonListElement[] = [];
  if (authenticatorButtonsWithGracePeriod.length) {
    authenticatorListElementWithGracePeriod.push({
      type: 'AuthenticatorButtonList',
      options: { buttons: authenticatorButtonsWithGracePeriod, dataSe: 'authenticator-enroll-list-grace-period' },
    });
  }

  const authenticatorListElementDueNow: AuthenticatorButtonListElement[] = [];
  if (authenticatorButtonsDueNow.length) {
    authenticatorListElementDueNow.push({
      type: 'AuthenticatorButtonList',
      options: { buttons: authenticatorButtonsDueNow, dataSe: 'authenticator-enroll-list' },
    });
  }

  const skipButton: ButtonElement = {
    type: 'Button',
    label: authenticatorListElementWithGracePeriod.length
      ? loc('oie.setup.remind.me.later', 'login')
      : loc('oie.optional.authenticator.button.title', 'login'),
    options: {
      type: ButtonType.SUBMIT,
      step: 'skip',
    },
  };

  // 3 situations - required soon + required now, all required soon, all required now
  // when grace periods are past required and should be treated as normal required
  const elements: UISchemaElement[] = [title, description];
  if (authenticatorListElementDueNow.length && authenticatorListElementWithGracePeriod.length) {
    elements.push(
      headingRequiredNow,
      ...authenticatorListElementDueNow,
      headingRequiredSoon,
      descriptionGracePeriod,
      ...(gracePeriodRequiredSoonCustomLink ? [gracePeriodRequiredSoonCustomLink] : []),
      ...authenticatorListElementWithGracePeriod,
    );
  } else if (authenticatorListElementWithGracePeriod.length) {
    elements.push(
      headingRequiredSoon,
      descriptionGracePeriod,
      ...(gracePeriodRequiredSoonCustomLink ? [gracePeriodRequiredSoonCustomLink] : []),
      ...authenticatorListElementWithGracePeriod,
    );
  } else {
    elements.push(
      headingNoGracePeriod,
      ...authenticatorListElementDueNow,
    );
  }
  if (skipStep) {
    elements.push(skipButton);
  }

  uischema.elements = elements;

  return formBag;
};
